"use client";

import { useIsMobile } from "@/hooks/useIsMobile";
import { useTranslationDetector } from "@/hooks/useTranslationDetector";
import {
  dateFormats,
  formatDate,
  getShortDayNames,
} from "@/utils/dateFormatter";
import { byPrefixAndName } from "@awesome.me/kit-12cf79935c/icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import clsx from "clsx";
import { useLocale } from "next-intl";
import { memo, useCallback, useMemo, useState } from "react";
import { DayPicker, getDefaultClassNames } from "react-day-picker";
import { ar, de, tr } from "react-day-picker/locale";
import DefaultDayButton from "./DefaultDayButton";

const CalendarRangeContent = memo(
  ({
    value,
    handleDateSelect,
    defaultMonth,
    getPriceByDate,
    currency,
    isRoundTrip,
    mobileDepartureClick,
    isFromDate,
    departureDate,
    mobileReturnClick,
    returnDate,
    isMultiCity = false,
    publicHolidays = [],
    dayButtonComponent,
    getDayMeta,
  }) => {
    const isMobile = useIsMobile();
    const locale = useLocale();
    const t = useTranslationDetector("bus");
    const defaultClassNames = getDefaultClassNames();

    const [hoveredDate, setHoveredDate] = useState(null);

    const defaultMonthValue = useMemo(() => {
      if (isMobile) {
        const now = new Date();
        return new Date(now.getFullYear(), now.getMonth());
      } else {
        const date = new Date(defaultMonth);
        return new Date(date.getFullYear(), date.getMonth());
      }
    }, [isMobile, defaultMonth]);

    const safeGetPriceByDate = useCallback(
      (d) => {
        try {
          return typeof getPriceByDate === "function"
            ? getPriceByDate(d)
            : null;
        } catch {
          return null;
        }
      },
      [getPriceByDate],
    );

    const memoizedDefaultDayButton = useCallback(
      (props) => (
        <DefaultDayButton
          {...props}
          getPriceByDate={safeGetPriceByDate}
          currency={currency}
          isHoliday={publicHolidays.some(
            (holiday) =>
              holiday.date ===
              formatDate(props.day.date, { format: "YYYY-MM-DD" }),
          )}
        />
      ),
      [safeGetPriceByDate, currency, publicHolidays],
    );

    const handleDayMouseEnter = useCallback(
      (date) => {
        const isSameDate =
          value?.from &&
          value?.to &&
          value.from.getTime() === value.to.getTime();
        if (
          value?.from &&
          (!value?.to || isSameDate) &&
          date &&
          date > value.from
        ) {
          setHoveredDate(date);
        } else if (!value?.from || (value?.to && !isSameDate)) {
          setHoveredDate(null);
        }
      },
      [value?.from, value?.to],
    );

    const handleDayMouseLeave = useCallback(() => {
      setHoveredDate(null);
    }, []);

    const modifiers = useMemo(() => {
      const isSameDate =
        value?.from && value?.to && value.from.getTime() === value.to.getTime();
      if (
        !value?.from ||
        !hoveredDate ||
        (value?.to && !isSameDate) ||
        hoveredDate <= value.from
      ) {
        return {};
      }
      const nextDay = new Date(value.from);
      nextDay.setDate(nextDay.getDate() + 1);
      return { hoverRange: { from: nextDay, to: hoveredDate } };
    }, [value?.from, value?.to, hoveredDate]);

    return (
      <div className="flex h-full flex-col gap-4 px-0 py-4 md:px-4">
        {!isMultiCity && (
          <div className="flex items-center justify-between gap-4 px-4 md:hidden md:px-0">
            <button
              className={clsx(
                "bg-primary-weaker flex h-14 w-1/2 flex-col items-start justify-center rounded-lg border-2 px-3 py-2",
                !departureDate
                  ? "border-primary bg-primary-weaker"
                  : "border-border bg-white",
              )}
            >
              <span className="text-xs">{t("departure")}</span>
              <span className="text-sm font-medium">
                {dateFormats.dayLongMonthShortDay(departureDate, locale)}
              </span>
            </button>
            <button
              className={clsx(
                "flex h-14 w-1/2 flex-col items-start justify-center rounded-lg border-2 px-3 py-2",
                !returnDate
                  ? "border-primary bg-primary-weaker"
                  : "border-border bg-white",
              )}
            >
              <span className="text-xs">{t("return")}</span>
              <span className="text-sm font-medium">
                {returnDate ? dateFormats.dayLongMonthShortDay(returnDate) : ""}
              </span>
            </button>
          </div>
        )}

        <div className="border-primary-weak flex items-center justify-between border-b px-4 md:hidden">
          {getShortDayNames(locale).map((day) => (
            <span className="w-[55px] py-2 text-center" key={day}>
              {day}
            </span>
          ))}
        </div>

        <div className="months-container h-full overflow-scroll px-0 pb-4 md:overflow-visible md:pb-0">
          <DayPicker
            key={`daypicker-${isMobile ? "mobile" : "desktop"}`}
            locale={
              locale === "tr"
                ? tr
                : locale === "ar"
                  ? ar
                  : locale === "de"
                    ? de
                    : tr
            }
            animate
            mode="range"
            selected={value}
            onSelect={handleDateSelect}
            // onDayMouseEnter={!isMobile ? handleDayMouseEnter : undefined}
            // onDayMouseLeave={!isMobile ? handleDayMouseLeave : undefined}
            // modifiers={!isMobile ? modifiers : {}}
            // modifiersStyles={{ hoverRange: { backgroundColor: "#F2F2F9" } }}
            numberOfMonths={isMobile ? 12 : 2}
            navLayout="around"
            hideWeekdays={isMobile ? true : false}
            formatters={{
              formatWeekdayName: (weekday, options) => {
                const label = formatDate(weekday, { locale, format: "ddd" }); // -> 3 karakter

                return label;
              },
            }}
            defaultMonth={defaultMonthValue}
            disabled={{ before: new Date() }}
            classNames={{
              months: `${defaultClassNames.months} justify-between !max-w-full !gap-2`,
              month: `${defaultClassNames.month} w-full md:w-auto`,
              month_grid: `${defaultClassNames.month_grid} w-full`,
              month_caption: `${defaultClassNames.month_caption} h-auto md:pb-0`,
              disabled: "text-foreground-weaker-subtext",
              today: "text-primary",
              day: "font-semibold w-[55px] h-[58px]",
              selected: "",
              weekday:
                "border-b border-primary-weak text-sm text-foreground-muted font-medium h-11",
              button_next:
                "absolute right-0 top-[13px] w-[9px] text-black cursor-pointer opacity-0 md:opacity-100",
              button_previous:
                "absolute left-0 top-[13px] w-[9px] text-black cursor-pointer opacity-0 md:opacity-100",
            }}
            components={{
              ...(dayButtonComponent
                ? {
                    DayButton: (props) =>
                      dayButtonComponent({
                        ...props,
                        dayMeta: getDayMeta?.(props.day?.date),
                      }),
                  }
                : { DayButton: memoizedDefaultDayButton }),
              Month: (props) => {
                const currentMonthHolidays = publicHolidays.filter(
                  (holiday) => {
                    const holidayDate = new Date(holiday.date);
                    const calendarDate = props.calendarMonth.date;
                    return (
                      holidayDate.getFullYear() ===
                        calendarDate.getFullYear() &&
                      holidayDate.getMonth() === calendarDate.getMonth()
                    );
                  },
                );
                return (
                  <div className="flex w-full flex-col items-center justify-between gap-2 md:w-auto">
                    <div className="w-full">{props.children}</div>
                    {currentMonthHolidays.length > 0 && (
                      <div className="flex w-full flex-col items-start gap-1 pl-[14px] md:pb-0">
                        {currentMonthHolidays.map((holiday) => (
                          <div
                            className={clsx(
                              "flex w-full items-center justify-start gap-[9px]",
                              !holiday.title && "hidden",
                            )}
                            key={holiday.date}
                          >
                            <span className="bg-tertiary-emphasis h-[6px] w-[6px] rounded-full"></span>
                            <span className="text-foreground-muted inline-flex items-center gap-1 text-sm">
                              <span>
                                {dateFormats.dayShortMonth(
                                  holiday.date,
                                  locale,
                                )}
                              </span>
                              <span className="max-w-[95%] truncate">
                                {holiday.title}
                              </span>
                            </span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              },
              NextMonthButton: (props) => (
                <FontAwesomeIcon
                  icon={byPrefixAndName.far["chevron-right"]}
                  {...props}
                />
              ),
              PreviousMonthButton: (props) => (
                <FontAwesomeIcon
                  icon={byPrefixAndName.far["chevron-left"]}
                  {...props}
                />
              ),
            }}
          />
        </div>
      </div>
    );
  },
);

CalendarRangeContent.displayName = "CalendarRangeContent";

export default CalendarRangeContent;
